<template>
    <div v-if="isVisible" class="LinkedInNoDomainDataAlert flex w-full">
        <CAlert class="w-full [&_.CAlert--content]:w-full" variant="warning">
            <div class="flex w-full flex-1 flex-col items-end gap-3">
                <div class="flex w-full gap-2.5">
                    <CIcon name="linkedin-outline" size="small" />

                    <span class="[&_em]:rounded-sm [&_em]:bg-secondary-500 [&_em]:p-0.5 [&_i]:text-xs [&_i]:italic">
                        <b>LinkedIn nám tieto údaje neposlal:</b>
                        <ul v-for="domain in missingDomains" :key="domain" class="pt-1">
                            <li class="pl-2 before:pr-1 before:content-['•']">
                                <em class="font-medium">{{ $t(`linkedInDomains.${domain}.title`, lang) }}</em>
                                {{ $t(`linkedInDomains.${domain}.location`, lang) }}
                            </li>
                        </ul>
                        <br />
                        Ak ich na LinkedIne v danej časti máš, chvíľku počkaj a klikni na "Obnoviť".
                        <br /><br />
                        <i>Ak sa tieto údaje nezobrazia ani po obnovení, počkaj 24 hodín a skús to znovu, prosím.</i>
                    </span>
                </div>

                <CButton
                    class="min-h-0! px-3! py-1.5! text-xs font-medium uppercase"
                    :disabled="isDisabled"
                    :loading="state === 'loading'"
                    variant="secondary"
                    @click="handleRefetch"
                >
                    <span ref="buttonRef">Obnoviť</span>
                </CButton>
            </div>
        </CAlert>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

import { EMemberSnapshotDataDomains } from '#shared/types/LinkedIn'
import type { LoadingState } from '#shared/types/LoadingState'
import { useLinkedin } from '@/composables/linkedin/useLinkedin'

const props = defineProps({
    lang: {
        type: String as PropType<'sk' | 'en'>,
        default: 'sk'
    },
    domains: {
        type: Array as PropType<EMemberSnapshotDataDomains[]>,
        required: true
    },
    connectionData: {
        type: Object as PropType<any | null>,
        default: null
    },
    profileData: {
        type: Object as PropType<any | null>,
        default: null
    },
    disabled: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['update:profile-data'])

const state = ref<LoadingState>('idle')
const waitingState = ref<LoadingState>('idle')
const buttonRef = ref<HTMLElement | null>(null)

const missingDomains = computed((): EMemberSnapshotDataDomains[] => {
    if (!props.profileData || !props.connectionData) return []

    const connectionDomains = props.connectionData.domains || []
    const profileDomains = props.profileData.domains || []

    return props.domains.filter((domain) => connectionDomains.includes(domain) && !profileDomains.includes(domain))
})

const isVisible = computed((): boolean => {
    return !!missingDomains.value.length
})

const isDisabled = computed((): boolean => {
    return props.disabled || state.value === 'loading' || waitingState.value === 'loading'
})

async function handleRefetch() {
    if (isDisabled.value || !isVisible.value || !props.profileData) return
    if (!buttonRef.value) return
    state.value = 'loading'

    try {
        const { refreshProfileData } = useLinkedin()
        const profileData = await refreshProfileData(props.profileData.domains, missingDomains.value)
        emit('update:profile-data', profileData)
        state.value = 'idle'

        waitingState.value = 'loading'
        let secondsLeft = 30
        const interval = setInterval(() => {
            if (secondsLeft > 0) {
                if (buttonRef.value) buttonRef.value.innerText = `Obnoviť o ${secondsLeft}`
                secondsLeft--
            } else {
                clearInterval(interval)
                if (buttonRef.value) buttonRef.value.innerText = 'Obnoviť'
                waitingState.value = 'idle'
            }
        }, 1_000)
    } catch (error) {
        state.value = 'error'
        waitingState.value = 'error'
    }
}
</script>
